/**
 * @file MessageComposer.tsx
 * @description Поле ответа клиенту (chat-window-design.md §1б, §4, §5, §7):
 * растущее поле (до 6 строк), Enter — отправить с физической клавиатуры
 * (на сенсорном Enter переносит строку), Shift+Enter — перенос; флажок
 * «Рекламное сообщение» с подсказкой, неактивен без согласия клиента;
 * полосы «ведёт другой» / «без ответственного» над полем и «архив» /
 * «только чтение» / «больше не назначен» вместо поля.
 *
 * Черновик живёт ЗДЕСЬ (`useDraft`), а не в родителе: ввод буквы
 * перерисовывает только поле, а не ленту и список. Вернуть текст в поле
 * (отказ по рекламе) — `restore` с новым `nonce`.
 */

import { Info, SendHorizontal } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import type { ConversationDetail } from "../../lib/api-chat";
import { CHAT_TEXT } from "./chat-texts";
import { BLOCKING_STRIPS, composerStripKind } from "./composer-strip-kind";
import { ComposerStrip } from "./ComposerStrip";
import { useDraft } from "./useMessageSender";

const MAX_HEIGHT_PX = 6 * 20 + 24;
/** Предел сервера на длину сообщения. */
const MAX_BODY_CHARS = 4_000;

/** Просьба вернуть текст в поле; `nonce` — повтор того же текста. */
export interface DraftRestore {
  body: string;
  nonce: number;
}

interface Props {
  detail: ConversationDetail | null;
  meUserId: string;
  lost: boolean;
  shopId: string;
  conversationId: string;
  restore: DraftRestore | null;
  onSend: (body: string, isMarketing: boolean) => void;
  busy: boolean;
  onTake: () => void;
  onUnarchive: () => void;
  onToList: () => void;
  /** Фокус в поле при открытии диалога. */
  autoFocus?: boolean;
}

/** Сенсорный экран без мыши: Enter переносит строку. */
function coarsePointer(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches === true;
}

export function MessageComposer(props: Props) {
  const { detail, meUserId, lost, onSend, busy, restore } = props;
  const [draft, onDraft] = useDraft(props.shopId, props.conversationId);
  const [marketing, setMarketing] = useState(false);
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);
  const marketingId = useId();
  const strip = composerStripKind(detail, meUserId, lost);
  const blocked = strip !== null && BLOCKING_STRIPS.has(strip);
  const consent = detail?.marketingConsent.granted ?? false;
  const ready = detail !== null && detail.actions.send && !blocked;

  // Высота по содержимому, до 6 строк.
  useEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [draft]);

  useEffect(() => {
    if (props.autoFocus && ready) fieldRef.current?.focus();
  }, [props.autoFocus, ready, detail?.id]);

  useEffect(() => setMarketing(false), [detail?.id]);

  // Отказ по рекламе — текст обратно в поле.
  useEffect(() => {
    if (restore) onDraft(restore.body);
  }, [restore, onDraft]);

  const submit = () => {
    const body = draft.trim();
    if (!ready || !body) return;
    onSend(body, marketing && consent);
    onDraft("");
    setMarketing(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing || coarsePointer()) return;
    event.preventDefault();
    submit();
  };

  const stripNode = strip ? (
    <ComposerStrip kind={strip} detail={detail} busy={busy} onTake={props.onTake} onUnarchive={props.onUnarchive} onToList={props.onToList} />
  ) : null;

  if (blocked) return <div className="shrink-0 border-t border-outline-variant p-2">{stripNode}</div>;

  return (
    <div className="flex shrink-0 flex-col gap-2 border-t border-outline-variant p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
      {stripNode}
      <div className="flex items-center gap-2 px-1">
        <Checkbox
          id={marketingId}
          checked={marketing && consent}
          disabled={!consent || !ready}
          onCheckedChange={(v) => setMarketing(v === true)}
        />
        <label htmlFor={marketingId} className="flex min-h-12 items-center whitespace-nowrap text-sm text-on-surface lg:min-h-8">
          {CHAT_TEXT.marketing}
        </label>
        <Tooltip>
          <TooltipTrigger
            render={(p) => (
              <button {...p} type="button" aria-label={CHAT_TEXT.marketingHint} className="inline-flex size-12 items-center justify-center rounded-md text-on-surface-variant outline-none focus-visible:ring-2 focus-visible:ring-ring lg:size-8">
                <Info className="size-4" aria-hidden />
              </button>
            )}
          />
          <TooltipContent className="max-w-72">{CHAT_TEXT.marketingHint}</TooltipContent>
        </Tooltip>
      </div>
      {!consent && detail ? <p className="-mt-2 px-1 text-xs text-on-surface-variant">{CHAT_TEXT.marketingNoConsent}</p> : null}
      <div className="flex items-end gap-2">
        <textarea
          ref={fieldRef}
          rows={1}
          value={draft}
          maxLength={MAX_BODY_CHARS}
          disabled={!ready}
          placeholder={CHAT_TEXT.composerPlaceholder}
          aria-label={CHAT_TEXT.composerPlaceholder}
          onChange={(e) => onDraft(e.target.value)}
          onKeyDown={onKeyDown}
          className="min-h-12 w-full resize-none rounded-lg bg-input px-3 py-3 text-base text-on-surface outline-none placeholder:text-on-surface-muted focus-visible:bg-surface-lowest focus-visible:[box-shadow:inset_0_-2px_0_var(--primary)] disabled:opacity-50 lg:min-h-11 lg:py-2.5 lg:text-sm"
        />
        <Button size="icon" className="size-12 shrink-0 lg:size-11" aria-label={CHAT_TEXT.send} disabled={!ready || !draft.trim()} onClick={submit}>
          <SendHorizontal className="size-5" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
